/**
 * Document Hooks
 * TanStack Query hooks for document management (RAG)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { documentRepo } from '@/lib/repositories'
import { analytics } from '@/lib/services/analytics'
import { AnalyticsEvents } from '@/lib/services/analytics-events'

// Query keys
export const DOCUMENTS_KEY = ['documents'] as const
export const DOCUMENT_KEY = (id: string) => ['document', id] as const

/**
 * Fetch all documents (admin view)
 */
export function useDocuments() {
  return useQuery({
    queryKey: DOCUMENTS_KEY,
    queryFn: () => documentRepo.getDocuments(),
    staleTime: 30_000, // 30 seconds
  })
}

/**
 * Fetch a single document by ID (for polling status)
 */
export function useDocument(documentId: string | null) {
  return useQuery({
    queryKey: DOCUMENT_KEY(documentId || ''),
    queryFn: () => documentRepo.getDocument(documentId!),
    enabled: !!documentId,
    refetchInterval: (query) => {
      // Poll every 3 seconds while processing, stop when done
      const doc = query.state.data
      if (doc?.status === 'processing') return 3000
      return false
    },
  })
}

/**
 * Upload a document
 */
export function useUploadDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: {
      file: File
      ticker?: string
      documentType?: 'ticker' | 'strategy'
    }) => documentRepo.uploadDocument(data),
    onSuccess: (_, data) => {
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_KEY })
      analytics.capture(AnalyticsEvents.ADMIN_DOCUMENT_UPLOADED, {
        document_type: data.documentType,
        has_ticker: !!data.ticker,
      })
    },
  })
}

/**
 * Delete a document
 */
export function useDeleteDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (documentId: string) => documentRepo.deleteDocument(documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_KEY })
      analytics.capture(AnalyticsEvents.ADMIN_DOCUMENT_DELETED)
    },
  })
}

/**
 * Hook to poll for processing documents
 * Automatically refetches the documents list while any doc is processing
 */
export function useDocumentsWithPolling() {
  return useQuery({
    queryKey: DOCUMENTS_KEY,
    queryFn: () => documentRepo.getDocuments(),
    staleTime: 10_000,
    refetchInterval: (query) => {
      const docs = query.state.data
      const hasProcessing = docs?.some((d) => d.status === 'processing')
      return hasProcessing ? 3000 : false
    },
  })
}
